import {
  getBrowserAuthHeaders,
  notifyBrowserAuthUnauthorized,
} from './browserAuthTransport';
import type {
  ConversationSuggestionsRequest,
  ConversationSuggestionsResponse,
} from '../types/conversationSuggestions';
import { API_BASE_URL } from '../utils/config';

export class ConversationSuggestionsRequestError extends Error {
  readonly status: number;

  constructor(status: number, detail: string) {
    super(detail);
    this.name = 'ConversationSuggestionsRequestError';
    this.status = status;
  }
}

const buildRequestPath = (sessionId: string): string => (
  `${API_BASE_URL}/sessions/${encodeURIComponent(sessionId)}/conversation-suggestions`
);

const readErrorMessage = async (response: Response): Promise<string> => {
  try {
    const body = await response.json();
    return typeof body?.detail === 'string'
      ? body.detail
      : 'Unable to generate conversation suggestions.';
  } catch {
    return 'Unable to generate conversation suggestions.';
  }
};

export const conversationSuggestionsService = {
  async generateSuggestions(
    sessionId: string,
    request: ConversationSuggestionsRequest,
    options: { signal?: AbortSignal } = {},
  ): Promise<ConversationSuggestionsResponse> {
    const response = await fetch(buildRequestPath(sessionId), {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...getBrowserAuthHeaders('POST'),
      },
      body: JSON.stringify(request),
      signal: options.signal,
    });

    if (response.status === 401) {
      notifyBrowserAuthUnauthorized();
    }

    if (!response.ok) {
      throw new ConversationSuggestionsRequestError(
        response.status,
        await readErrorMessage(response),
      );
    }

    return response.json() as Promise<ConversationSuggestionsResponse>;
  },
};